import { useEffect, useState } from 'react';
import ElectronHelper from 'src/common/electron/ElectronHelper';
import { RetailElectronAPI } from 'src/common/electron/RetailElectronAPI';
import { CustomerDisplayLifecycle, CUSTOMER_DISPLAY_CHANNEL } from './useCustomerDisplay';

export default function useCustomerDisplayData<t>(initialState: t): [t] {
  const [, setPort] = useState<MessagePort>();
  const [data, setData] = useState<t>(initialState);

  useEffect(() => {
    const getAndSetPort = async (): Promise<void> => {
      let messagePort: MessagePort;

      if (ElectronHelper.isElectron()) {
        messagePort = await getMessagePortForElectron();
      } else {
        messagePort = await getMessagePortForBrowser();
      }

      setPort(messagePort);
      messagePort.postMessage('Customer Display is all wired up!');
      messagePort.onmessage = (event: MessageEvent) => {
        setData(event.data);
      };
    };

    //executing async function but not waiting
    getAndSetPort();
  }, []);

  return [data];
}

async function getMessagePortForBrowser(): Promise<MessagePort> {
  return new Promise(res => {
    const portMessageListener = (event: MessageEvent): void => {
      if (event.data !== CUSTOMER_DISPLAY_CHANNEL) {
        return;
      }
      const messagePort = (event as MessageEvent).ports[0];
      res(messagePort);

      // cleaning up message listener after first fire
      window.removeEventListener('message', portMessageListener);
    };

    // wiring up listener for a message channel
    window.addEventListener('message', portMessageListener, false);
    // letting main window know we are ready to receive the message channel
    window.opener?.postMessage(CustomerDisplayLifecycle.Loaded, window.location.origin);
  });
}

async function getMessagePortForElectron(): Promise<MessagePort> {
  const custDisplayController = RetailElectronAPI.getCustomerDisplayController();
  const mainWindowController = RetailElectronAPI.getMainWindowController();

  // needed until we can upgrade electron to latest version so we can pass a MessagePort through IPC
  // this is creating a temp interface in which this component interfaces with
  const tempChannel = new MessageChannel();

  // on data from the electron main thread, pipe it in to the temp channel
  custDisplayController.registerCustomerDisplayDataListener((event, data) => {
    tempChannel.port1.postMessage(data);
  });

  // on data from the channel, pass to electron main thread to be forwarded in to main screen
  tempChannel.port1.onmessage = event => {
    mainWindowController.sendToMainWindow(event.data);
  };

  // sending the 'all ready' command to main POS so it can start sending data
  mainWindowController.sendToMainWindow(CustomerDisplayLifecycle.Loaded);
  return Promise.resolve(tempChannel.port2);
}
